import type { PublishLifecycleState } from '@course-authoring/shared-types';

import type { PublishableContentKind } from '../api/publishApi';

export type PublishResumeOperation = 'publish' | 'publish-update';

export interface PublishResumeIntent {
  schemaVersion: 1;
  createdAt: number;
  userId: string;
  routePath: string;
  contentKind: PublishableContentKind;
  contentId: string;
  operation: PublishResumeOperation;
  lifecycleState: Extract<PublishLifecycleState, 'draft' | 'editing'>;
  accountId: number | null;
  observedBuildId: string;
}

export interface PublishResumeContext {
  userId: string;
  routePath: string;
  contentKind: PublishableContentKind;
  contentId: string;
  lifecycleState: PublishLifecycleState;
  accountId: number | null;
}

type PublishResumeIntentInput = Omit<PublishResumeIntent, 'schemaVersion' | 'createdAt'>;

interface StorageLike {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

export const PUBLISH_RESUME_INTENT_TTL_MS = 2 * 60 * 1000;
export const PUBLISH_RESUME_INTENT_STORAGE_KEY = 'authorai.publishResumeIntent';
export const AUTHORING_BUILD_QUERY_PARAM = '_authoraiBuild';

const resolveStorage = (storage?: StorageLike): StorageLike => storage ?? window.sessionStorage;

const isPublishResumeIntent = (value: unknown): value is PublishResumeIntent => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<PublishResumeIntent>;
  const hasValidLifecycle = candidate.lifecycleState === 'draft' || candidate.lifecycleState === 'editing';
  const hasValidOperation = candidate.operation === 'publish' || candidate.operation === 'publish-update';
  const hasCompatibleLifecycle =
    (candidate.operation === 'publish' && candidate.lifecycleState === 'draft') ||
    (candidate.operation === 'publish-update' && candidate.lifecycleState === 'editing');

  return (
    candidate.schemaVersion === 1 &&
    typeof candidate.createdAt === 'number' &&
    Number.isFinite(candidate.createdAt) &&
    typeof candidate.userId === 'string' &&
    candidate.userId.length > 0 &&
    typeof candidate.routePath === 'string' &&
    candidate.routePath.startsWith('/') &&
    (candidate.contentKind === 'course' || candidate.contentKind === 'lesson') &&
    typeof candidate.contentId === 'string' &&
    candidate.contentId.length > 0 &&
    hasValidOperation &&
    hasValidLifecycle &&
    hasCompatibleLifecycle &&
    (candidate.accountId === null ||
      (typeof candidate.accountId === 'number' && Number.isInteger(candidate.accountId))) &&
    typeof candidate.observedBuildId === 'string' &&
    candidate.observedBuildId.length > 0
  );
};

export function storePublishResumeIntent(
  input: PublishResumeIntentInput,
  options: { storage?: StorageLike; now?: number } = {},
): boolean {
  const intent: PublishResumeIntent = {
    schemaVersion: 1,
    createdAt: options.now ?? Date.now(),
    ...input,
  };

  try {
    resolveStorage(options.storage).setItem(PUBLISH_RESUME_INTENT_STORAGE_KEY, JSON.stringify(intent));
    return true;
  } catch {
    return false;
  }
}

export function consumePublishResumeIntent(
  context: PublishResumeContext,
  options: { storage?: StorageLike; now?: number } = {},
): PublishResumeIntent | null {
  let serialized: string | null;

  try {
    const storage = resolveStorage(options.storage);
    serialized = storage.getItem(PUBLISH_RESUME_INTENT_STORAGE_KEY);
    storage.removeItem(PUBLISH_RESUME_INTENT_STORAGE_KEY);
  } catch {
    return null;
  }

  if (!serialized) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized);
  } catch {
    return null;
  }

  if (!isPublishResumeIntent(parsed)) {
    return null;
  }

  const age = (options.now ?? Date.now()) - parsed.createdAt;
  if (age < 0 || age > PUBLISH_RESUME_INTENT_TTL_MS) {
    return null;
  }

  if (
    parsed.userId !== context.userId ||
    parsed.routePath !== context.routePath ||
    parsed.contentKind !== context.contentKind ||
    parsed.contentId !== context.contentId ||
    parsed.lifecycleState !== context.lifecycleState ||
    parsed.accountId !== context.accountId
  ) {
    return null;
  }

  return parsed;
}

export function buildAuthoringReloadUrl(buildId: string, currentHref: string): string {
  const url = new URL(currentHref);
  url.searchParams.set(AUTHORING_BUILD_QUERY_PARAM, buildId);
  return url.toString();
}

export function reloadForCurrentAuthoringBuild(buildId: string): void {
  window.location.replace(buildAuthoringReloadUrl(buildId, window.location.href));
}
